import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Modal, Spinner } from "flowbite-react";
import { FaWhatsapp } from "react-icons/fa";
import {
  HiCheck,
  HiCog6Tooth,
  HiEllipsisVertical,
  HiPaperAirplane,
  HiPaperClip,
  HiSparkles,
  HiUserCircle,
} from "react-icons/hi2";
import { io } from "socket.io-client";
import { useApplication } from "../contexts/ApplicationContext";
import axios from "../config/axiosInstance";
import { COMPANY, WHATSAPP } from "../config/apiConfig";
import Input from "../components/inputs/Input";
import "./WhatsAppChatPageModernV2.css";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const SkeletonShimmer = ({ className }) => (
  <div className={cx("wa-shimmer", className)} />
);

const ConversationSkeletonRow = () => (
  <div className="wa-skeleton-row">
    <SkeletonShimmer className="wa-skeleton-avatar" />
    <div className="wa-skeleton-main">
      <div className="wa-skeleton-header">
        <SkeletonShimmer className="wa-skeleton-name" />
        <SkeletonShimmer className="wa-skeleton-time" />
      </div>
      <div className="wa-skeleton-preview-wrap">
        <SkeletonShimmer className="wa-skeleton-preview" />
      </div>
    </div>
  </div>
);

const ChatBubbleSkeleton = ({ side = "left" } = {}) => {
  const isRight = side === "right";
  return (
    <div className={cx("wa-chat-skeleton-row", isRight && "is-right")}>
      <div className="wa-chat-skeleton-bubble">
        <SkeletonShimmer className="wa-chat-skeleton-line wa-chat-skeleton-line-short" />
        <div className="wa-chat-skeleton-lines">
          <SkeletonShimmer className="wa-chat-skeleton-line wa-chat-skeleton-line-long" />
          <SkeletonShimmer className="wa-chat-skeleton-line wa-chat-skeleton-line-mid" />
        </div>
      </div>
    </div>
  );
};

const fmtTime = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
});

const formatDateTime = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return fmtTime.format(date);
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const getDateLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const resolveInitials = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return "WA";
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const AvatarCircle = ({ conv, size = "small" }) => {
  const sizeClass = size === "large" ? "wa-avatar-large" : "wa-avatar-small";
  return (
    <div className={cx("wa-avatar", sizeClass)}>
      {resolveInitials(conv?.name || conv?.contact || "WA")}
    </div>
  );
};

const MessageBubble = ({ message, isMe }) => {
  const isPending = String(message?.status || "").toLowerCase() === "pending";
  const isFailed = String(message?.status || "").toLowerCase() === "failed";

  return (
    <div className={cx("wa-message-row", isMe && "is-me")}>
      <div className={cx("wa-message-bubble", isMe ? "is-me" : "is-them")}>
        <p className="wa-message-text">{message?.body || "[mensagem]"}</p>
        <div className="wa-message-meta">
          <span>{formatDateTime(message?.createdAt)}</span>
          {isMe ? (
            <div className="wa-message-status">
              {isFailed ? (
                <span className="wa-message-status-failed">!</span>
              ) : isPending ? (
                <HiCheck className="wa-message-status-pending" />
              ) : (
                <div className="wa-message-status-read">
                  <HiCheck className="wa-message-status-read-icon first" />
                  <HiCheck className="wa-message-status-read-icon" />
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ChatHistoryWithDateSeparators = ({ chatHistory }) => {
  if (!chatHistory.length) return null;
  let lastDate = null;

  return chatHistory.map((message) => {
    const msgDate = message.createdAt || message.sentAt || message.receivedAt;
    const showDate = !lastDate || !isSameDay(msgDate, lastDate);
    lastDate = msgDate;

    return (
      <React.Fragment key={message.id}>
        {showDate && (
          <div className="wa-date-separator-wrap">
            <span className="wa-date-separator-pill">
              {getDateLabel(msgDate)}
            </span>
          </div>
        )}
        <MessageBubble
          message={message}
          isMe={message.direction === "outbound"}
        />
      </React.Fragment>
    );
  });
};

export default function WhatsAppChatPageModernV2() {
  const { user } = useApplication();
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [exchangingCode, setExchangingCode] = useState(false);
  const [embeddedConfig, setEmbeddedConfig] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [conversationResponseFilter, setConversationResponseFilter] =
    useState("all");
  const [statusAlert, setStatusAlert] = useState({
    show: false,
    type: "success",
    message: "",
  });
  const [isMetaConfigModalOpen, setIsMetaConfigModalOpen] = useState(false);
  const [realtimeTick, setRealtimeTick] = useState(0);
  const selectedConversationRef = useRef(null);

  const [formData, setFormData] = useState({
    whatsappEmbeddedSignupCode: "",
    whatsappAccessToken: "",
    whatsappPhoneNumberId: "",
    whatsappBusinessAccountId: "",
    whatsappPhoneNumber: "",
    whatsappWebhookVerifyToken: "",
  });

  const tenantId = Number(user?.companyId || 0) || null;
  const isConfigured = Boolean(tenantId);

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (item) => String(item.id) === String(selectedConversationId),
      ) || null,
    [conversations, selectedConversationId],
  );

  const conversationResponseFilterCounts = useMemo(() => {
    const unread = conversations.filter(
      (item) => Number(item.unreadCount || 0) > 0,
    ).length;
    const answered = conversations.filter(
      (item) => Number(item.unreadCount || 0) === 0,
    ).length;
    return { all: conversations.length, answered, unread };
  }, [conversations]);

  const filteredConversations = useMemo(() => {
    if (conversationResponseFilter === "unread") {
      return conversations.filter((item) => Number(item.unreadCount || 0) > 0);
    }
    if (conversationResponseFilter === "answered") {
      return conversations.filter(
        (item) => Number(item.unreadCount || 0) === 0,
      );
    }
    return conversations;
  }, [conversations, conversationResponseFilter]);

  const webhookUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/whatsapp/meta/webhook`
      : "/api/whatsapp/meta/webhook";

  const socketBaseUrl = useMemo(() => {
    const baseUrl = String(axios?.defaults?.baseURL || "");
    if (baseUrl) return baseUrl.replace(/\/api\/?$/, "");
    if (typeof window !== "undefined") return window.location.origin;
    return "";
  }, []);

  const showAlert = (type, message) => {
    setStatusAlert({ show: true, type, message });
    setTimeout(() => {
      setStatusAlert({ show: false, type: "success", message: "" });
    }, 3000);
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const loadConfig = async () => {
    if (!tenantId) return;
    try {
      setLoadingConfig(true);
      const [companyResponse, embeddedResponse] = await Promise.all([
        axios.get(COMPANY.GET_ACTIVE),
        axios.get(WHATSAPP.META_EMBEDDED_SIGNUP_CONFIG(tenantId)),
      ]);

      const company = companyResponse?.data?.data || {};
      const embedded = embeddedResponse?.data?.data || null;

      setFormData((prev) => ({
        ...prev,
        whatsappAccessToken: company.whatsappAccessToken || "",
        whatsappPhoneNumberId: company.whatsappPhoneNumberId || "",
        whatsappBusinessAccountId: company.whatsappBusinessAccountId || "",
        whatsappPhoneNumber: company.whatsappPhoneNumber || "",
        whatsappWebhookVerifyToken: company.whatsappWebhookVerifyToken || "",
      }));
      setEmbeddedConfig(embedded);
    } catch (error) {
      showAlert(
        "failure",
        "Nao foi possivel carregar a configuracao do WhatsApp.",
      );
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadConversations = async () => {
    if (!tenantId) return;
    try {
      setLoadingConversations(true);
      const response = await axios.get(WHATSAPP.LIST_CONVERSATIONS(tenantId));
      const items = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      setConversations(items);

      if (!items.length) {
        setSelectedConversationId(null);
        setMessages([]);
        return;
      }

      const stillExists = items.some(
        (item) => String(item.id) === String(selectedConversationId),
      );
      if (!stillExists) {
        setSelectedConversationId(String(items[0].id));
      }
    } catch (error) {
      showAlert("failure", "Nao foi possivel carregar conversas do WhatsApp.");
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (conversation) => {
    if (!tenantId || !conversation?.contact) return;
    try {
      setLoadingMessages(true);
      const response = await axios.get(
        WHATSAPP.CONVERSATION_DETAILS(tenantId, conversation.contact),
      );
      const items = Array.isArray(response?.data?.data?.messages)
        ? response.data.data.messages
        : [];
      setMessages(items);
    } catch (error) {
      showAlert(
        "failure",
        "Nao foi possivel carregar as mensagens da conversa.",
      );
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }
    loadConversationMessages(selectedConversation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    if (!tenantId || !socketBaseUrl) return undefined;

    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const socket = io(socketBaseUrl, {
      transports: ["websocket"],
      auth: {
        token: `Bearer ${token}`,
        tenantId,
      },
    });

    socket.on("whatsapp:update", () => {
      setRealtimeTick((prev) => prev + 1);
    });

    return () => {
      socket.disconnect();
    };
  }, [tenantId, socketBaseUrl]);

  useEffect(() => {
    if (!realtimeTick) return;

    loadConversations();

    const currentConversation = selectedConversationRef.current;
    if (currentConversation?.contact) {
      loadConversationMessages(currentConversation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realtimeTick]);

  const handleSaveConfig = async (event) => {
    event.preventDefault();
    if (!tenantId) return;

    try {
      setSavingConfig(true);
      await axios.put(WHATSAPP.CONFIG(tenantId), {
        whatsappAccessToken: formData.whatsappAccessToken,
        whatsappPhoneNumberId: formData.whatsappPhoneNumberId,
        whatsappBusinessAccountId: formData.whatsappBusinessAccountId,
        whatsappPhoneNumber: formData.whatsappPhoneNumber,
        whatsappWebhookVerifyToken: formData.whatsappWebhookVerifyToken,
      });
      showAlert("success", "Configuracao do WhatsApp salva com sucesso.");
      await loadConfig();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Erro ao salvar configuracao do WhatsApp.";
      showAlert("failure", message);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleExchangeCode = async () => {
    const code = String(formData.whatsappEmbeddedSignupCode || "").trim();
    if (!tenantId || !code) {
      showAlert("failure", "Informe o code do Embedded Signup.");
      return;
    }

    try {
      setExchangingCode(true);
      const response = await axios.post(
        WHATSAPP.META_EMBEDDED_SIGNUP_EXCHANGE(tenantId),
        { code },
      );
      const data = response?.data?.data || {};
      setFormData((prev) => ({
        ...prev,
        whatsappAccessToken:
          data.whatsappAccessToken || prev.whatsappAccessToken,
        whatsappPhoneNumberId:
          data.whatsappPhoneNumberId || prev.whatsappPhoneNumberId,
        whatsappBusinessAccountId:
          data.whatsappBusinessAccountId || prev.whatsappBusinessAccountId,
        whatsappPhoneNumber:
          data.whatsappPhoneNumber || prev.whatsappPhoneNumber,
        whatsappWebhookVerifyToken:
          data.whatsappWebhookVerifyToken || prev.whatsappWebhookVerifyToken,
      }));
      showAlert(
        "success",
        "Code processado com sucesso. Salve para confirmar.",
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Falha ao processar code do Embedded Signup.";
      showAlert("failure", message);
    } finally {
      setExchangingCode(false);
    }
  };

  const handleSendMessage = async () => {
    const text = String(messageDraft || "").trim();
    if (!tenantId || !selectedConversation?.contact || !text) return;

    try {
      setSendingMessage(true);
      await axios.post(WHATSAPP.SEND(tenantId), {
        to: selectedConversation.contact,
        message: text,
      });
      setMessageDraft("");
      await loadConversationMessages(selectedConversation);
      await loadConversations();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Erro ao enviar mensagem no WhatsApp.";
      showAlert("failure", message);
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div className="wa-page-grid">
      <section className="wa-left-panel">
        <div className="wa-left-header">
          <div className="wa-left-title-row">
            <div className="wa-left-title-wrap">
              <FaWhatsapp className="wa-brand-icon" size={20} />
              <p className="wa-left-title">WhatsApp</p>
            </div>
          </div>

          {statusAlert.show ? (
            <Alert color={statusAlert.type} className="wa-status-alert">
              {statusAlert.message}
            </Alert>
          ) : null}

          <div className="wa-filter-row">
            <button
              type="button"
              onClick={() => setConversationResponseFilter("all")}
              className={cx(
                "wa-filter-btn",
                conversationResponseFilter === "all" && "is-active-all",
              )}
            >
              Todas ({conversationResponseFilterCounts.all})
            </button>
            <button
              type="button"
              onClick={() => setConversationResponseFilter("answered")}
              className={cx(
                "wa-filter-btn",
                conversationResponseFilter === "answered" &&
                  "is-active-answered",
              )}
            >
              Respondidas ({conversationResponseFilterCounts.answered})
            </button>
            <button
              type="button"
              onClick={() => setConversationResponseFilter("unread")}
              className={cx(
                "wa-filter-btn",
                conversationResponseFilter === "unread" && "is-active-unread",
              )}
            >
              Nao lidas ({conversationResponseFilterCounts.unread})
            </button>
          </div>
        </div>

        <div className="wa-scroll wa-conversations-list">
          {loadingConversations ? (
            <div className="wa-skeleton-list">
              {Array.from({ length: 8 }).map((_, idx) => (
                <ConversationSkeletonRow key={idx} />
              ))}
            </div>
          ) : null}

          {!loadingConversations && filteredConversations.length === 0 ? (
            <div className="wa-empty-conversation">
              Nenhuma conversa registrada ainda. Aguarde o primeiro contato.
            </div>
          ) : null}

          {!loadingConversations &&
            filteredConversations.map((conv, index) => {
              const selected =
                String(conv.id) === String(selectedConversationId);
              const normalizedConvKey = String(
                conv.id || conv.contact || index,
              );

              return (
                <div
                  key={normalizedConvKey}
                  className="wa-conversation-row-wrap"
                >
                  <button
                    onClick={() => setSelectedConversationId(String(conv.id))}
                    className={cx(
                      "wa-conversation-row",
                      selected && "is-selected",
                    )}
                  >
                    <AvatarCircle conv={conv} size="small" />
                    <div className="wa-conversation-content">
                      <div className="wa-conversation-topline">
                        <p className="wa-conversation-name">
                          {conv.name || conv.contact}
                        </p>
                        <span className="wa-conversation-time">
                          {formatDateTime(conv.lastMessageAt)}
                        </span>
                      </div>
                      <p className="wa-conversation-preview">
                        {conv.lastMessage || "Sem mensagens"}
                      </p>
                    </div>
                    {Number(conv.unreadCount || 0) > 0 ? (
                      <div className="wa-unread-badge">{conv.unreadCount}</div>
                    ) : null}
                  </button>
                  <div className="wa-conversation-menu-slot">
                    <span className="wa-conversation-menu-btn">
                      <HiEllipsisVertical className="wa-conversation-menu-icon" />
                    </span>
                  </div>
                </div>
              );
            })}
        </div>

        <div className="wa-config-panel">
          <button
            type="button"
            className="wa-config-trigger"
            onClick={async () => {
              await loadConfig();
              setIsMetaConfigModalOpen(true);
            }}
            disabled={!isConfigured || loadingConfig}
          >
            {loadingConfig ? (
              <Spinner size="xs" />
            ) : (
              <HiCog6Tooth className="wa-icon-sm" />
            )}{" "}
            Configuracoes Meta
          </button>
        </div>
      </section>

      <section className="wa-chat-panel">
        {selectedConversation ? (
          <>
            <div className="wa-chat-header">
              <div className="wa-chat-header-main">
                <button
                  type="button"
                  className="wa-contact-avatar-button"
                  aria-label="Abrir informações do contato"
                >
                  <AvatarCircle conv={selectedConversation} size="large" />
                </button>
                <div>
                  <p className="wa-contact-name">
                    {selectedConversation.name ||
                      selectedConversation.contact ||
                      "Selecione uma conversa"}
                  </p>
                  <div className="wa-contact-meta-row">
                    <div className="wa-contact-meta-item">
                      <HiUserCircle className="wa-contact-meta-icon" />
                      <span className="wa-contact-meta-text">
                        Atendimento ativo
                      </span>
                    </div>
                    <Badge color="gray" size="xs">
                      IA responsável: Não
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="wa-chat-header-actions">
                <button
                  type="button"
                  className="wa-circle-action-btn"
                  aria-label="Mais ações"
                >
                  <HiEllipsisVertical className="wa-circle-action-icon" />
                </button>
              </div>
            </div>

            <div className="wa-scroll wa-chat-history">
              {loadingMessages ? (
                messages.length ? (
                  <div className="wa-chat-loading-with-content">
                    <div className="wa-chat-loading-faded-history">
                      <ChatHistoryWithDateSeparators chatHistory={messages} />
                    </div>
                    <div className="wa-chat-loading-overlay">
                      <div className="wa-chat-loading-card">
                        <div className="wa-chat-loading-row">
                          <Spinner size="md" />
                          <div>
                            <div className="wa-chat-loading-title">
                              Carregando conversa...
                            </div>
                            <div className="wa-chat-loading-subtitle">
                              Buscando histórico mais recente
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="wa-chat-skeleton-stack">
                    <ChatBubbleSkeleton side="left" />
                    <ChatBubbleSkeleton side="right" />
                    <ChatBubbleSkeleton side="left" />
                    <ChatBubbleSkeleton side="right" />
                    <ChatBubbleSkeleton side="left" />
                  </div>
                )
              ) : messages.length === 0 ? (
                <div className="wa-chat-empty-state">
                  Nenhuma mensagem registrada ainda. Use o campo abaixo para
                  iniciar o atendimento.
                </div>
              ) : (
                <ChatHistoryWithDateSeparators chatHistory={messages} />
              )}
            </div>

            <form
              className="wa-composer"
              onSubmit={(event) => {
                event.preventDefault();
                handleSendMessage();
              }}
            >
              <div className="wa-composer-main-row">
                <div className="wa-composer-input-wrap">
                  <textarea
                    className={cx(
                      "wa-composer-textarea",
                      (!selectedConversation || !isConfigured) && "is-disabled",
                    )}
                    rows={1}
                    value={messageDraft}
                    onChange={(event) => setMessageDraft(event.target.value)}
                    placeholder="Escreva a mensagem..."
                    disabled={!selectedConversation || !isConfigured}
                  />

                  <div className="wa-composer-plus-slot">
                    <button
                      type="button"
                      className={cx(
                        "wa-composer-plus-btn",
                        (!selectedConversation || !isConfigured) &&
                          "is-disabled",
                      )}
                      title="Ações"
                      disabled={!selectedConversation || !isConfigured}
                    >
                      <span className="wa-composer-plus-symbol">+</span>
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={
                    !selectedConversation ||
                    sendingMessage ||
                    !isConfigured ||
                    !messageDraft.trim()
                  }
                >
                  {sendingMessage ? (
                    <span className="wa-send-btn-content">
                      <Spinner size="sm" /> Enviando
                    </span>
                  ) : (
                    <span className="wa-send-btn-content">
                      <HiPaperAirplane className="wa-send-icon" /> Enviar
                    </span>
                  )}
                </Button>
              </div>

              <div className="wa-composer-secondary-row">
                <div className="wa-composer-quick-actions">
                  <button
                    type="button"
                    className="wa-composer-quick-btn"
                    disabled
                  >
                    <HiPaperClip className="wa-quick-icon" /> Anexo
                  </button>
                  <button
                    type="button"
                    className="wa-composer-quick-btn"
                    disabled
                  >
                    <HiSparkles className="wa-quick-icon" /> IA
                  </button>
                </div>
              </div>
            </form>
          </>
        ) : (
          <div className="wa-chat-empty-panel">
            Selecione uma conversa para iniciar o atendimento.
          </div>
        )}
      </section>

      <Modal
        show={isMetaConfigModalOpen}
        onClose={() => setIsMetaConfigModalOpen(false)}
        size="3xl"
      >
        <Modal.Header className="py-2 text-base">
          Configurações Meta WhatsApp
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <div className="rounded-2xl border border-gray-200 bg-white p-3 text-xs text-gray-700 shadow-sm">
              <p className="font-semibold">Webhook URL</p>
              <p className="break-all">{webhookUrl}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input
                label="Phone ID"
                name="whatsappPhoneNumberId"
                register={() => ({
                  value: formData.whatsappPhoneNumberId,
                  onChange: (event) =>
                    updateField("whatsappPhoneNumberId", event.target.value),
                })}
              />
              <Input
                label="Business ID"
                name="whatsappBusinessAccountId"
                register={() => ({
                  value: formData.whatsappBusinessAccountId,
                  onChange: (event) =>
                    updateField(
                      "whatsappBusinessAccountId",
                      event.target.value,
                    ),
                })}
              />
              <Input
                label="Access Token"
                name="whatsappAccessToken"
                register={() => ({
                  value: formData.whatsappAccessToken,
                  onChange: (event) =>
                    updateField("whatsappAccessToken", event.target.value),
                })}
              />
              <Input
                label="Phone Number"
                name="whatsappPhoneNumber"
                register={() => ({
                  value: formData.whatsappPhoneNumber,
                  onChange: (event) =>
                    updateField("whatsappPhoneNumber", event.target.value),
                })}
              />
            </div>
            <Input
              label="Webhook Verify Token"
              name="whatsappWebhookVerifyToken"
              register={() => ({
                value: formData.whatsappWebhookVerifyToken,
                onChange: (event) =>
                  updateField("whatsappWebhookVerifyToken", event.target.value),
              })}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end pt-2 pb-3">
          <Button color="gray" onClick={() => setIsMetaConfigModalOpen(false)}>
            Fechar
          </Button>
          <Button
            onClick={(event) => handleSaveConfig(event)}
            isProcessing={savingConfig}
            processingSpinner={<Spinner size="xs" />}
            disabled={savingConfig}
          >
            Salvar configuracoes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
